import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Music, Sparkles, Play, Pause, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** Soundtrack picker: AI suggestions from the built-in library + custom upload. */
export default function SoundtrackCard({ eventId, event, onChanged }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [selecting, setSelecting] = useState("");
  const [uploading, setUploading] = useState(false);
  const [playingId, setPlayingId] = useState("");
  const audioRef = useRef(null);

  const suggest = async () => {
    setLoadingSuggest(true);
    try {
      const { data } = await api.post(`/events/${eventId}/music/suggest`);
      setSuggestions(data.suggestions || []);
      if (!data.suggestions?.length) toast.info("No suggestions right now — try again");
    } catch {
      toast.error("Could not get suggestions");
    } finally {
      setLoadingSuggest(false);
    }
  };

  const togglePreview = (track) => {
    if (playingId === track.id) {
      audioRef.current?.pause();
      setPlayingId("");
      return;
    }
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    a.src = track.url;
    a.volume = 0.7;
    a.play().catch(() => toast.error("Preview unavailable"));
    a.onended = () => setPlayingId("");
    setPlayingId(track.id);
  };

  const selectTrack = async (track) => {
    setSelecting(track.id);
    try {
      await api.post(`/events/${eventId}/music/select`, { track_id: track.id });
      toast.success(`"${track.title}" is now your soundtrack`);
      onChanged();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not select track");
    } finally {
      setSelecting("");
    }
  };

  const uploadMusic = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/events/${eventId}/music`, fd);
      toast.success("Background music added");
      onChanged();
    } catch {
      toast.error("Music upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="bg-white border-[var(--em-line)]">
      <CardContent className="p-6">
        <p className="font-mono-label text-[var(--em-sage)]">Soundtrack</p>
        <h3 className="font-serif-em text-xl mt-3">Background music</h3>
        <p className="text-sm text-[var(--em-ink-soft)] mt-1">Mixed gently under the voices, with a soft fade at the end.</p>

        {event.music_url && (
          <div className="mt-3 text-xs text-[var(--em-sage)] flex items-start gap-1.5" data-testid="music-current">
            <Check size={14 } className="mt-0.5 shrink-0" />
            <span>{event.music_credit ? event.music_credit.replace(/^Music: /, "") : "Custom music attached"}</span>
          </div>
        )}

        <Button
          data-testid="music-suggest-btn"
          variant="outline"
          disabled={loadingSuggest}
          onClick={suggest}
          className="mt-4 w-full justify-start"
        >
          {loadingSuggest ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          {loadingSuggest ? "Listening to your story…" : "AI suggest music"}
        </Button>

        {suggestions.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="music-suggestions">
            {suggestions.map((t) => (
              <div key={t.id} data-testid={`music-suggestion-${t.id}`} className="border border-[var(--em-line)] rounded-xl p-3 bg-[var(--em-bg)]">
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`music-preview-${t.id}`}
                    onClick={() => togglePreview(t)}
                    className="w-8 h-8 rounded-full bg-[var(--em-night)] text-white grid place-items-center shrink-0"
                    aria-label="Preview"
                  >
                    {playingId === t.id ? <Pause size={13} /> : <Play size={13} className="ml-0.5" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{t.title}</p>
                    <p className="text-[11px] text-[var(--em-ink-soft)]">{t.mood}</p>
                  </div>
                  <Button
                    data-testid={`music-use-${t.id}`}
                    size="sm"
                    disabled={!!selecting}
                    onClick={() => selectTrack(t)}
                    className={`rounded-full h-8 text-xs ${event.music_track_id === t.id ? "bg-[var(--em-sage)]" : "bg-[var(--em-clay)] hover:bg-[var(--em-clay-deep)]"} text-white`}
                  >
                    {selecting === t.id ? <Loader2 size={12} className="animate-spin" /> : event.music_track_id === t.id ? "Selected" : "Use"}
                  </Button>
                </div>
                {t.reason && <p className="text-[11px] italic text-[var(--em-ink-soft)] mt-2">{t.reason}</p>}
              </div>
            ))}
          </div>
        )}

        <label className="inline-flex items-center gap-2 cursor-pointer mt-4">
          <input data-testid="music-input" type="file" accept="audio/*" className="hidden" onChange={uploadMusic} />
          <span className="inline-flex items-center h-10 px-5 rounded-full border border-[var(--em-line)] bg-white hover:bg-[var(--em-bg)] text-sm">
            <Music className="mr-2 h-4 w-4" />
            {uploading ? "Uploading…" : event.music_url ? "Upload your own instead" : "Or upload your own"}
          </span>
        </label>
      </CardContent>
    </Card>
  );
}
